import { useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { RiMapPinLine } from '@remixicon/react';
import { STAGE, type ElementOf } from '@atnt/shared';
import { cn } from '@/shared/lib/cn';
import { useStageOptional } from '../stage/Stage';
import { dp } from '../units';
import { useScene } from './scene';
import '../engine.css';

/**
 * Belgi (marker). The element's (x, y) is the **dot centre** (§4.7), so scaling keeps the dot on the background
 * feature. Viewer look (14:7285 "moyka"): pulsing 60/44 rings, white teardrop pin, glass label pill that overlaps
 * the pin by 16 px; the label flips to the left when it would leave the stage.
 */
export function MarkerView({ element, children }: { element: ElementOf<'marker'>; children?: ReactNode }) {
  const { variant, mode, sections, onOpenSection } = useScene();
  const linked = element.linkSectionId ? sections.find((s) => s.id === element.linkSectionId) : undefined;
  const clickable = Boolean(linked && onOpenSection && mode !== 'edit');
  const open = () => linked && onOpenSection?.(linked.id);

  return (
    <div
      data-element-id={element.id}
      data-element-type="marker"
      className="absolute"
      style={{ left: dp(element.x), top: dp(element.y), transform: element.scale !== 1 ? `scale(${element.scale})` : undefined, transformOrigin: '0 0' }}
    >
      {variant === 'viewer' ? (
        <ViewerMarker element={element} clickable={clickable} onOpen={open} linkedName={linked?.name} />
      ) : (
        <CanvasMarker element={element} clickable={clickable} onOpen={open} />
      )}
      {children}
    </div>
  );
}

interface MarkerBodyProps {
  element: ElementOf<'marker'>;
  clickable: boolean;
  onOpen: () => void;
  linkedName?: string;
}

/**
 * Label side: right by default; left when the label would leave the stage or cover another marker's pin and the
 * left side is free (14:7285 "Favqulodda suv tashlash inshooti"). Measured in design px; re-measured when the
 * label resizes (web fonts).
 */
function useLabelFlip(element: ElementOf<'marker'>, labelRef: RefObject<HTMLSpanElement | null>) {
  const stage = useStageOptional();
  const { markers } = useScene();
  const [flip, setFlip] = useState(false);
  const ui = stage?.uiScale ?? 1;
  const stageWidth = stage?.width ?? STAGE.width;
  const others = markers.filter((m) => m.id !== element.id);
  const othersKey = others.map((m) => `${m.x},${m.y},${m.scale}`).join(';');

  useLayoutEffect(() => {
    const el = labelRef.current;
    if (!el) return;
    const { x, y, scale } = element;
    const anchors = othersKey ? othersKey.split(';').map((s) => s.split(',').map(Number) as [number, number, number]) : [];
    const blocked = (from: number, to: number) => anchors.some(([mx, my, ms]) => mx > from && mx < to && Math.abs(my - y) < 32 * Math.max(scale, ms));
    const measure = () => {
      const width = (el.offsetWidth / (ui || 1)) * scale; // design px, pin overlap included
      const rightBad = x + width > stageWidth - 8 || blocked(x, x + width + 16);
      const leftOk = x - width >= 8 && !blocked(x - width - 16, x);
      setFlip(rightBad && leftOk);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [element, labelRef, othersKey, ui, stageWidth]);
  return flip;
}

function ViewerMarker({ element, clickable, onOpen, linkedName }: MarkerBodyProps) {
  const labelRef = useRef<HTMLSpanElement>(null);
  const autoFlip = useLabelFlip(element, labelRef);
  // `labelSide` (editor "Yorliq tomoni"): explicit left / right, `auto` (default) flips on overflow / collisions
  const flip = element.labelSide === 'left' ? true : element.labelSide === 'right' ? false : autoFlip;

  const Tag = clickable ? 'button' : 'div';
  return (
    <Tag
      {...(clickable ? { type: 'button' as const, onClick: onOpen, 'aria-label': linkedName ? `${element.label} — ${linkedName}` : element.label } : {})}
      className={cn('group absolute top-0 left-0 block font-display', clickable && 'cursor-pointer')}
    >
      {/* pulse rings 60 (30 %) and 44 (50 %) in Other/05b4ff + an expanding ripple */}
      <span aria-hidden className="absolute -top-30 -left-30 size-60 rounded-full bg-other-05b4ff opacity-30" />
      <span aria-hidden className="absolute -top-22 -left-22 size-44 rounded-full bg-other-05b4ff opacity-50" />
      <span aria-hidden className="atnt-marker-ripple absolute -top-22 -left-22 size-44 rounded-full border-2 border-other-05b4ff" />
      {/* label pill: h31, glass (blur 16), radius on the far side, padding 0/16/0/24 (mirrored when flipped) */}
      <span
        ref={labelRef}
        className={cn(
          'glass-viewer absolute -top-16 flex h-31 items-center text-base leading-none font-semibold whitespace-nowrap text-white transition-colors group-hover:bg-white-200',
          flip ? 'right-0 rounded-l-20 pr-24 pl-16' : 'left-0 rounded-r-20 pr-16 pl-24',
        )}
      >
        {element.label}
      </span>
      {/* white teardrop 32×36 + Other/05b4ff disc 30 + map pin 16 */}
      <svg aria-hidden viewBox="0 0 32 36" className="absolute -top-16 -left-16 h-36 w-32 overflow-visible drop-shadow-[0_0_0.125rem_#00000033]">
        <path d="M16 0C24.84 0 32 7.16 32 16c0 7.6-5.3 13.95-12.4 15.6L17.3 35.2a1.5 1.5 0 0 1-2.6 0l-2.3-3.6C5.3 29.95 0 23.6 0 16 0 7.16 7.16 0 16 0Z" fill="#fff" />
        <circle cx="16" cy="16" r="15" fill="#05b4ff" />
        <path
          d="M16 9.25a4.75 4.75 0 0 0-4.75 4.75c0 3.55 4.75 8 4.75 8s4.75-4.45 4.75-8A4.75 4.75 0 0 0 16 9.25Zm0 6.5a1.75 1.75 0 1 1 0-3.5 1.75 1.75 0 0 1 0 3.5Z"
          fill="none"
          stroke="#fff"
          strokeWidth="1.4"
          strokeLinejoin="round"
        />
      </svg>
    </Tag>
  );
}

function CanvasMarker({ element, clickable, onOpen }: MarkerBodyProps) {
  // 317:7059: pill h30 r20, Secondary BG, 1 px Blue/1000, padding 5/12/5/8, gap 7; dot 18 Blue/1000; SB 13.
  // Dot centre sits at (8 + 9, 15) → shift the pill so the anchor is the dot centre. `labelSide: 'left'` mirrors it.
  const Tag = clickable ? 'button' : 'div';
  const left = element.labelSide === 'left';
  return (
    <Tag
      {...(clickable ? { type: 'button' as const, onClick: onOpen } : {})}
      className={cn(
        'absolute -top-15 flex h-30 items-center gap-7 rounded-20 border border-blue-1000 bg-secondary-bg-1000 py-5 font-sans whitespace-nowrap',
        left ? '-right-17 flex-row-reverse pr-8 pl-12' : '-left-17 pr-12 pl-8',
        clickable && 'cursor-pointer',
      )}
    >
      <span className="flex size-18 shrink-0 items-center justify-center rounded-full bg-blue-1000">
        <RiMapPinLine className="size-11 text-white" aria-hidden />
      </span>
      <span className="text-13 leading-auto font-semibold text-white">{element.label}</span>
    </Tag>
  );
}
